import { useState, useRef, useCallback } from 'react';
import { compressAll, type CompressedImage } from '@/pages/product-listing/utils/imageCompress';

interface ImageUploaderProps {
  images: CompressedImage[];
  onChange: (images: CompressedImage[]) => void;
  videoFile?: File | null;
  videoPreviewUrl?: string;
  onVideoChange?: (file: File | null, previewUrl: string) => void;
}

const MAX_IMAGES = 4;
const MAX_VIDEO_MB = 20;

export default function ImageUploader({ images, onChange, videoFile, videoPreviewUrl, onVideoChange }: ImageUploaderProps) {
  const [compressing, setCompressing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoError, setVideoError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) return;
    const selected = Array.from(files).slice(0, remaining);
    const validFiles = selected.filter(f => f.type.startsWith('image/'));
    if (validFiles.length === 0) return;
    setCompressing(true);
    try {
      const compressed = await compressAll(validFiles);
      onChange([...images, ...compressed]);
    } finally {
      setCompressing(false);
    }
  }, [images, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const removeImage = (idx: number) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= images.length) return;
    const updated = [...images];
    const [item] = updated.splice(from, 1);
    updated.splice(to, 0, item);
    onChange(updated);
  };

  // ── Video Upload Handler ──
  const handleVideoSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onVideoChange) return;

    setVideoError('');

    if (!file.type.startsWith('video/')) {
      setVideoError('Sirf video file upload karo (MP4, WebM, MOV)');
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setVideoError(`Video ${MAX_VIDEO_MB}MB se choti honi chahiye. Compress karke try karo.`);
      return;
    }

    setVideoUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      onVideoChange(file, previewUrl);
    } finally {
      setVideoUploading(false);
    }
    e.target.value = '';
  }, [onVideoChange]);

  const removeVideo = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    onVideoChange?.(null, '');
    setVideoError('');
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      {/* ── IMAGES ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-800 font-semibold text-sm">Product Images</p>
            <p className="text-gray-400 text-xs mt-0.5">
              Max 4 photos · Auto compressed · First = thumbnail
            </p>
          </div>
          <span className={`text-xs font-bold px-2 py-1 rounded-lg ${images.length >= MAX_IMAGES ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
            {images.length}/{MAX_IMAGES}
          </span>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group aspect-square">
              <img
                src={img.dataUrl}
                alt={`Product ${idx + 1}`}
                className={`w-full h-full object-cover rounded-xl border-2 transition-all ${idx === 0 ? 'border-orange-400' : 'border-gray-200'}`}
              />
              {idx === 0 && (
                <div className="absolute top-1 left-1 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md leading-none">THUMB</div>
              )}
              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-md leading-none">{img.sizeKB}KB</div>
              <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {idx > 0 && (
                  <button onClick={() => moveImage(idx, idx - 1)} className="w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center text-gray-700 hover:bg-white transition-colors cursor-pointer">
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                )}
                <button onClick={() => removeImage(idx)} className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center text-white hover:bg-red-600 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
                {idx < images.length - 1 && (
                  <button onClick={() => moveImage(idx, idx + 1)} className="w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center text-gray-700 hover:bg-white transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>
          ))}

          {images.length < MAX_IMAGES && (
            <div
              onClick={() => !compressing && inputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                dragOver ? 'border-orange-400 bg-orange-50' : compressing ? 'border-gray-200 bg-gray-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50'
              }`}
            >
              {compressing ? (
                <><div className="w-5 h-5 rounded-full border-2 border-orange-400 border-t-transparent animate-spin mb-1"></div><span className="text-[9px] text-orange-500 font-semibold text-center leading-tight">Compressing...</span></>
              ) : (
                <><i className="ri-image-add-line text-gray-400 text-xl mb-1"></i><span className="text-[9px] text-gray-400 font-medium text-center leading-tight">{images.length === 0 ? 'Add Photos' : 'Add More'}</span></>
              )}
            </div>
          )}

          {Array.from({ length: Math.max(0, MAX_IMAGES - images.length - 1) }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square rounded-xl border-2 border-dashed border-gray-100 bg-gray-50/50" />
          ))}
        </div>

        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => processFiles(e.target.files)} onClick={e => { (e.target as HTMLInputElement).value = ''; }} />

        {images.length > 0 && (
          <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2 flex items-center gap-2">
            <i className="ri-image-edit-line text-green-500 text-sm shrink-0"></i>
            <div className="flex-1 min-w-0">
              <p className="text-green-700 text-xs font-semibold">Auto Compressed</p>
              <p className="text-green-600 text-[10px]">
                {images.reduce((s, i) => s + i.originalSizeKB, 0)}KB → {images.reduce((s, i) => s + i.sizeKB, 0)}KB
                {' '}({Math.round((1 - images.reduce((s, i) => s + i.sizeKB, 0) / Math.max(1, images.reduce((s, i) => s + i.originalSizeKB, 0))) * 100)}% saved)
              </p>
            </div>
            <i className="ri-check-line text-green-500 shrink-0"></i>
          </div>
        )}
      </div>

      {/* ── VIDEO (OPTIONAL - Collapsible) ── */}
      {onVideoChange && (
        <div className="border-t border-gray-100 pt-4">
          {!videoPreviewUrl && !videoFile && (
            <button
              onClick={() => videoInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/50 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all cursor-pointer"
            >
              <i className="ri-video-add-line text-lg"></i>
              <span className="text-sm font-semibold">Product Video Add Karo (Optional)</span>
            </button>
          )}
          
          {(videoPreviewUrl || videoFile) && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                    <i className="ri-video-line text-purple-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-gray-800 font-semibold text-sm leading-none">
                      Product Video
                      <span className="ml-2 text-[10px] font-normal text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Optional</span>
                    </p>
                    <p className="text-gray-400 text-[10px] mt-0.5">Max 20MB · MP4/WebM</p>
                  </div>
                </div>
                <button 
                  onClick={removeVideo} 
                  className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  title="Remove video and skip"
                >
                  <i className="ri-close-line mr-1"></i>Skip Video
                </button>
              </div>

              {/* Video Preview */}
              {videoPreviewUrl ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-purple-200 bg-black" style={{ height: '140px' }}>
                  <video
                    src={videoPreviewUrl}
                    className="w-full h-full object-contain"
                    controls
                    playsInline
                    muted
                    preload="metadata"
                  />
                  {/* Duration badge */}
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center gap-1">
                    <i className="ri-video-line text-[10px]"></i>
                    5-sec Video
                  </div>
                  {/* File size badge */}
                  {videoFile && (
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px]">
                      {(videoFile.size / 1024 / 1024).toFixed(1)}MB
                    </div>
                  )}
                </div>
              ) : (
                /* Upload Zone */
                <label
                  className={`w-full flex flex-col items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                    videoUploading
                      ? 'border-purple-200 bg-purple-50 text-purple-400'
                      : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-gray-400 hover:text-purple-600'
                  }`}
                >
                  {videoUploading ? (
                    <>
                      <div className="w-6 h-6 rounded-full border-2 border-purple-400 border-t-transparent animate-spin"></div>
                      <span className="text-xs font-semibold text-purple-500">Processing...</span>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                        <i className="ri-video-upload-line text-xl"></i>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-semibold">Tap to upload product video</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">MP4 / WebM · Max 5 seconds · Max 20MB</p>
                      </div>
                    </>
                  )}
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/*"
                    className="hidden"
                    onChange={handleVideoSelect}
                    disabled={videoUploading}
                  />
                </label>
              )}

              {/* Error */}
              {videoError && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{videoError}
                </p>
              )}
            </>
          )}
          
          {/* Hidden input for initial trigger */}
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/*"
            className="hidden"
            onChange={handleVideoSelect}
            disabled={videoUploading}
          />
        </div>
      )}
    </div>
  );
}
